import { BarChart, SimpleGrid } from '~/design_system'
import { DashboardPageContainer, DashboardPageHeader } from '~/dashboards/common'
import { contractorStatus } from '~/dashboards/filters/config'
import { useDeepEqualEffect2 } from '~/hooks/useDeepEqualEffect'
import { useVendorVisible } from '~/api/work_order'
import { usePermittedFilters } from '~/hooks/usePermittedFilters'
import { useMsg } from '~/common/localizationUtils'
import { getVendorFilter } from '~/work_order_list/filtersConfig'
import { useChartFormat } from '~/design_system/components/charts/format'
import { ChartCard } from '~/dashboards/ChartCard'
import { NamedQueryFilter } from '~/custom_reports/types'
import { updateDashboardQueryParams } from './hooks'
import { getFilterParams } from './filters/utils'
import { useDashboardFilters } from './filters/hooks'
import filterAdapters from './filters/adapters/headcount'

const CONTRACTOR_ACTIVE = '2'

export const defaultFilters = [{ id: 'status', operator: 'equal', value: CONTRACTOR_ACTIVE }]

const filtersConfig = () => [
  { ...contractorStatus, id: 'status', defaultValue: [CONTRACTOR_ACTIVE] },
  { ...getVendorFilter(), id: 'vendor_name' }
]

const useUpdateFiltersEffect = (filters: NamedQueryFilter[]) => {
  const queryFilters = getFilterParams(filters, filterAdapters)

  const bulkDashboardQueryParams = {
    headcount_by_vendor: { filters: queryFilters },
    headcount_by_rating: { filters: queryFilters }
  }

  useDeepEqualEffect2(() => {
    updateDashboardQueryParams(bulkDashboardQueryParams)
  }, [bulkDashboardQueryParams])
}

export const Headcount = () => {
  const msg = useMsg()
  const permittedFilters = usePermittedFilters(filtersConfig())

  return (
    <>
      <DashboardPageHeader title={msg('term.headcount')} />
      <DashboardPageContainer filtersConfig={permittedFilters}>
        <Charts />
      </DashboardPageContainer>
    </>
  )
}

const Charts = () => {
  const { isVendorVisible } = useVendorVisible()
  const filters = useDashboardFilters()
  useUpdateFiltersEffect(filters)

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
      {isVendorVisible && <HeadCountByVendor filters={filters} />}
      <HeadCountByRating filters={filters} />
    </SimpleGrid>
  )
}

type HeadCountProps = {
  filters: NamedQueryFilter[]
}

export const HeadCountByVendor = ({ filters }: HeadCountProps) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)

  useDeepEqualEffect2(() => {
    updateDashboardQueryParams({ headcount_by_vendor: { filters: queryFilters } })
  }, [queryFilters])

  return (
    <ChartCard
      queryId="HeadCountByVendor"
      reportKey="headcount_by_vendor"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        format: 'number',
        xAxisLabel: msg('term.headcount'),
        yAxisLabel: msg('term.vendor', { count: 1 }),
        subTitle: msg('dashboards.chart.headcount_by_vendor.sub_title'),
        title: msg('dashboards.chart.headcount_by_vendor.title')
      }}
    />
  )
}

const HeadCountByRating = ({ filters }: HeadCountProps) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)
  const axisFormat = useChartFormat('number')

  return (
    <ChartCard
      queryId="HeadCountByRating"
      reportKey="headcount_by_rating"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        format: 'number',
        xAxisLabel: msg('term.headcount'),
        xAxisFormat: axisFormat,
        yAxisLabel: msg('term.rating'),
        layout: 'vertical',
        title: msg('dashboards.chart.headcount_by_rating.title'),
        subTitle: msg('dashboards.chart.headcount_by_rating.sub_title')
      }}
    />
  )
}
